import {
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
} from 'react'
import { createPortal } from 'react-dom'

import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'

import { TOOLBAR_HEIGHT } from '../../nav'
import { useLayoutStore } from '../hooks/useLayoutStore'
import { useSidebarLayout } from '../hooks/useSidebarLayout'

export { SidebarLayoutDrawer, DRAWER_WIDTHS }

const DRAWER_WIDTHS = {
  sm: 480,
  md: 720,
  lg: 960,
}

type Props = PropsWithChildren<{
  open: boolean
  onClose: () => void
  title: ReactNode
  size?: keyof typeof DRAWER_WIDTHS
  actions?: ReactNode
  'data-testid'?: string
}>

const SidebarLayoutDrawer = ({
  open,
  onClose,
  title,
  size = 'md',
  actions,
  'data-testid': dataTestId,
  children,
}: Props) => {
  const id = useId()
  const width = DRAWER_WIDTHS[size]
  const { drawerSlot } = useSidebarLayout()
  const { activeDrawer, registerDrawer, unregisterDrawer } = useLayoutStore()
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  })

  useLayoutEffect(() => {
    if (!open) return

    registerDrawer({ id, width, onClose: () => onCloseRef.current() })

    return () => unregisterDrawer(id)
  }, [open, id, width, registerDrawer, unregisterDrawer])

  return (
    // Also while closing, so the content stays visible while the panel animates
    // out
    drawerSlot &&
    activeDrawer?.id === id &&
    createPortal(
      <Box
        data-testid={dataTestId}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            paddingLeft: 2,
            paddingRight: 2,
            height: TOOLBAR_HEIGHT,
          }}
        >
          <Typography variant="h4" sx={{ m: 0, fontWeight: 'bold' }}>
            {title}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {actions}

            <IconButton
              data-testid="close-button"
              aria-label="Sluiten"
              onClick={onClose}
              sx={{ color: 'black' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        <Box role="presentation" sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          {children}
        </Box>
      </Box>,
      drawerSlot,
    )
  )
}
